import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
  Alert,
  AlertDescription,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { SearchBar } from '../components/ListControls';
import {
  CLIENT_BORROW_INFO,
  CLIENT_MARGIN_ACCOUNTS,
  CLIENT_FACILITIES,
} from '../data/mockAdapter';

interface Facility {
  id: string;
  name: string;
  collateralTokens: string[];
  settlementTokens: string[];
  interestRate: string;
  maxLTV: string;
  liquidationThreshold: string;
  utilization: string;
  tvl: string;
  borrowed: string;
  status: 'active' | 'paused';
}

interface MarginAccount {
  id: string;
  facilityId: string;
  facilityName: string;
  settlementToken: string;
  collateralTokens: string;
  collateralAmount: string;
  collateralValue: string;
  borrowedAmount: string;
  borrowedValue: string;
  interestAccrued: string;
  healthFactor: string;
  status: 'healthy' | 'at-risk' | 'liquidated';
}

const FACILITIES: Facility[] = CLIENT_FACILITIES.map((facility) => ({
  id: facility.id,
  name: facility.name,
  collateralTokens: facility.collateralTokens,
  settlementTokens: facility.settlementTokens,
  interestRate: facility.interestRate,
  maxLTV: facility.maxLTV,
  liquidationThreshold: facility.liquidationThreshold,
  utilization: facility.utilization,
  tvl: facility.tvl,
  borrowed: facility.borrowed,
  status: facility.status,
}));

const MARGIN_ACCOUNTS: MarginAccount[] = CLIENT_MARGIN_ACCOUNTS.map((account) => ({
  id: account.accountId,
  facilityId: account.facilityId,
  facilityName: account.facilityName,
  settlementToken: account.settlementToken,
  collateralTokens: account.collateralTokenText,
  collateralAmount: account.collateralAmountText,
  collateralValue: account.collateralValue,
  borrowedAmount: account.borrowedAmount,
  borrowedValue: account.borrowedValue,
  interestAccrued: account.interestAccrued,
  healthFactor: account.healthFactor,
  status: account.status,
}));

interface BorrowInfo {
  borrowed: number;
  available: number;
  maxBorrow: number;
  limitUsedPct: number;
}

const BORROW_INFO: Record<string, BorrowInfo> = CLIENT_BORROW_INFO;

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

const WARNING_TEXT =
  'On-chain checks remain authoritative and may reject transactions if state changes.';

const DEFI_OPPORTUNITIES = [
  { id: 'aave-horizon', name: 'Aave Horizon', description: 'Institutional-grade lending for RWA-backed positions', estimatedApy: '4.2%', tokens: ['USDC', 'USDT'], category: 'Lending', url: 'https://aave.com/horizon' },
  { id: 'mellow', name: 'Mellow Protocol', description: 'Automated vault strategies for optimized stablecoin yield', estimatedApy: '6.8%', tokens: ['USDC', 'USDT', 'DAI'], category: 'Vaults', url: 'https://mellow.finance' },
  { id: 'pendle', name: 'Pendle Finance', description: 'Yield tokenization and trading for fixed-rate exposure', estimatedApy: '8.5%', tokens: ['USDC', 'USDT'], category: 'Yield Trading', url: 'https://pendle.finance' },
  { id: 'morpho', name: 'Morpho', description: 'Peer-to-peer lending optimization with higher rates', estimatedApy: '5.1%', tokens: ['USDC'], category: 'Lending', url: 'https://morpho.org' },
  { id: 'ethena', name: 'Ethena', description: 'Synthetic dollar protocol with delta-neutral yield strategies', estimatedApy: '12.0%', tokens: ['USDC', 'USDT'], category: 'Synthetic', url: 'https://ethena.fi' },
  { id: 'ondo', name: 'Ondo Finance', description: 'Tokenized treasuries and real-world asset yield', estimatedApy: '5.3%', tokens: ['USDC'], category: 'RWA', url: 'https://ondo.finance' },
] as const;

function formatNum(n: number): string {
  if (n === 0) return '0';
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function getLimitBarColor(pct: number): string {
  if (pct < 80) return 'bg-green-500';
  if (pct <= 100) return 'bg-yellow-500';
  return 'bg-red-500';
}

function getLimitTextColor(pct: number): string {
  if (pct < 80) return 'text-green-600';
  if (pct <= 100) return 'text-yellow-600';
  return 'text-red-600';
}

function getStatusColor(status: MarginAccount['status']): string {
  if (status === 'healthy') return 'bg-green-100 text-green-700';
  if (status === 'at-risk') return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

// Resolve the query string to a specific accountId.
// Prefer ?accountId=... when present. Fall back to ?facilityId=... by picking
// the healthiest account in that facility, so old links keep working and
// callsites that only know the facility (e.g. facility-detail row buttons)
// don't have to switch.
function resolveAccountIdFromParams(params: URLSearchParams): string | null {
  const explicit = params.get('accountId');
  if (explicit) return explicit;
  const fid = params.get('facilityId');
  if (!fid) return null;
  const candidates = MARGIN_ACCOUNTS.filter((a) => a.facilityId === fid);
  if (candidates.length === 0) return null;
  const healthy = candidates.find((a) => a.status === 'healthy');
  return (healthy ?? candidates[0]).id;
}

export function BorrowPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    () => resolveAccountIdFromParams(searchParams),
  );
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [search, setSearch] = useState('');

  const account = MARGIN_ACCOUNTS.find((a) => a.id === selectedAccountId);
  const facility = account ? FACILITIES.find((v) => v.id === account.facilityId) : undefined;
  const info = account ? BORROW_INFO[account.facilityId] : null;
  const parsedAmount = parseFloat(amount) || 0;

  const filteredAccounts = useMemo(
    () =>
      MARGIN_ACCOUNTS.filter((a) => {
        const q = search.toLowerCase();
        return a.facilityName.toLowerCase().includes(q) || a.collateralTokens.toLowerCase().includes(q);
      }),
    [search],
  );

  function selectAccount(accountId: string) {
    setSelectedAccountId(accountId);
    setSearchParams({ accountId });
    setAmount('');
  }

  function goBack() {
    setSelectedAccountId(null);
    setSearchParams({});
    setAmount('');
    setShowInfo(false);
  }

  function handleMax() {
    if (info) setAmount(info.available.toString());
  }

  function handleExercise() {
    if (!info || parsedAmount <= 0 || parsedAmount > info.available) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setAmount('');
      toast.success('Credit exercised successfully — explore DeFi opportunities to deploy your capital');
      goBack();
    }, 2000);
  }

  if (account && facility && info) {
    const newBorrowed = info.borrowed + parsedAmount;
    const newLimitPct =
      info.maxBorrow > 0
        ? (newBorrowed / info.maxBorrow) * 100
        : newBorrowed > 0
          ? 100
          : 0;
    const displayPct = parsedAmount > 0 ? newLimitPct : info.limitUsedPct;

    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Positions
        </button>

        <div className="rounded-lg border border-green-500 bg-green-50 p-4 text-sm text-green-800">
          <div className="flex items-start gap-2">
            <Icon name="streamline:warning-triangle" className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{WARNING_TEXT}</span>
          </div>
        </div>

        <Card>
          <CardHeader className="p-6">
            <CardTitle>Exercise Credit ({account.settlementToken})</CardTitle>
            <CardDescription>
              Borrow against your margin account ({facility.name})
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Amount to exercise</label>
                <span className="text-sm text-muted-foreground">
                  Balance: {formatNum(info.available)} {account.settlementToken}
                </span>
              </div>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="any"
                  className={`${INPUT_CLASS} pr-24`}
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">
                    {account.settlementToken}
                  </span>
                  <button
                    type="button"
                    onClick={handleMax}
                    className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Borrow Limit Used</span>
                <span className={`font-medium ${getLimitTextColor(displayPct)}`}>
                  {displayPct.toFixed(1)}%
                </span>
              </div>
              <div className="h-3 rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${getLimitBarColor(displayPct)}`}
                  style={{ width: `${Math.min(displayPct, 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Borrowed: {formatNum(parsedAmount > 0 ? newBorrowed : info.borrowed)}{' '}
                  {account.settlementToken}
                </span>
                <span>
                  Available:{' '}
                  {formatNum(Math.max(0, info.available - parsedAmount))}{' '}
                  {account.settlementToken}
                </span>
              </div>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Interest Rate: {facility.interestRate} APR
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={goBack}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                disabled={
                  isSubmitting || parsedAmount <= 0 || parsedAmount > info.available
                }
                onClick={handleExercise}
              >
                {isSubmitting ? (
                  <>
                    <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Exercise Credit'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-6">
            <button
              type="button"
              onClick={() => setShowInfo(!showInfo)}
              className="flex w-full items-center justify-between"
            >
              <CardTitle className="text-base">
                Important Information &amp; Regulatory Notices
              </CardTitle>
              {showInfo ? (
                <Icon name="streamline:interface-arrows-button-up-arrow-up-keyboard" className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-5 w-5 text-muted-foreground" />
              )}
            </button>
          </CardHeader>
          {showInfo && (
            <CardContent className="p-6 pt-0 space-y-5">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-4 w-4 text-muted-foreground" />
                  Regulatory Compliance
                </div>
                <p className="text-sm text-muted-foreground">
                  This credit facility operates under applicable financial regulations. All
                  borrowers must maintain compliance with KYC/AML requirements. Transactions
                  may be subject to regulatory review.
                </p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="streamline:interface-content-fire-lit-flame-torch-trending" className="h-4 w-4 text-muted-foreground" />
                  Liquidation Risk
                </div>
                <p className="text-sm text-muted-foreground">
                  If your health factor drops below the liquidation threshold, your
                  collateral may be partially or fully liquidated to cover outstanding debt.
                  Monitor your positions regularly.
                </p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-4 w-4 text-muted-foreground" />
                  Interest Accrual
                </div>
                <p className="text-sm text-muted-foreground">
                  Interest accrues continuously on all outstanding positions. Rates are
                  variable and may change based on market conditions and facility utilization.
                </p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="streamline:interface-file-text-text-common-file" className="h-4 w-4 text-muted-foreground" />
                  Credit Facility Terms
                </div>
                <p className="text-sm text-muted-foreground">
                  By exercising credit, you agree to the terms of the credit facility. Early
                  repayment is permitted without penalty. Failure to maintain adequate
                  collateral may result in position liquidation.
                </p>
              </div>
              <p className="text-xs text-muted-foreground border-t border-border pt-4">
                This platform is provided &quot;as is&quot; without warranties of any kind.
                Users are responsible for their own investment decisions. Past performance is
                not indicative of future results. Consult your financial advisor before
                participating in credit facilities.
              </p>
            </CardContent>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Borrow</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Draw down against the collateral deposited in your margin accounts.
        </p>
      </div>

      <Alert>
        <Icon name="streamline:warning-triangle" className="h-4 w-4" />
        <AlertDescription>{WARNING_TEXT}</AlertDescription>
      </Alert>

      {/* DeFi Opportunities */}
      <div>
        <h2 className="text-lg font-semibold">Deploy Capital &mdash; DeFi Opportunities</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Explore what you can do with borrowed capital. Choose a strategy, then select a margin account below to borrow.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {DEFI_OPPORTUNITIES.map((opp) => (
            <a key={opp.id} href={opp.url} target="_blank" rel="noopener noreferrer" className="block transition-shadow hover:shadow-md rounded-xl">
              <Card>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="streamline:graph-bar-increase" className="h-4 w-4 text-primary" />
                      </div>
                      <h3 className="font-semibold">{opp.name}</h3>
                    </div>
                    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                      {opp.estimatedApy} APY
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{opp.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      {opp.tokens.map((t) => (
                        <span key={t} className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium">{t}</span>
                      ))}
                      <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">{opp.category}</span>
                    </div>
                    <Icon name="streamline:share-link" className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search margin accounts..." className="sm:w-64" />

      {filteredAccounts.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No margin accounts found matching your search.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredAccounts.map((acct) => {
            const v = FACILITIES.find((x) => x.id === acct.facilityId);
            if (!v) return null;
            const bi = BORROW_INFO[acct.facilityId];

            return (
              <Card key={acct.id}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">{acct.facilityName}</h3>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(acct.status)}`}
                    >
                      {acct.status}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Collateral: {acct.collateralAmount}
                  </p>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Borrowed</span>
                      <p className="font-semibold">{acct.borrowedAmount}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Health Factor</span>
                      <p className="font-semibold">{acct.healthFactor}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Available</span>
                      <p className="font-semibold text-green-600">
                        {bi ? formatNum(bi.available) : '0'} {acct.settlementToken}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Settlement</span>
                      <p className="font-semibold">{acct.settlementToken}</p>
                    </div>
                  </div>

                  <Button className="w-full" onClick={() => selectAccount(acct.id)}>
                    Borrow →
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
